import {PopoverProps} from '@material-ui/core';
import {MuiPickersUtilsProvider, KeyboardDatePicker} from '@material-ui/pickers';
import dayjs, {Dayjs} from 'dayjs';
import DayJsUtils from '@date-io/dayjs';
import React, {useRef} from 'react';
import {Nil} from '../model/SharedTypes';
import {
  errorClassName,
  fieldClassName,
  fieldWrapperClassName,
  inputClasses,
  labelClasses
} from '../nxGenericTextField/NxGenericTextField';
import styles from './NxDatePicker.scss';
import {logMissingOnChange} from '../inputError';
import {isNil} from 'lodash/fp';

export const DEFAULT_DATE_PICKER_DISPLAY_FORMAT = 'MM/DD/YYYY'
export const DEFAULT_DATE_PICKER_VALUE_FORMAT = 'YYYY-MM-DD'

export interface NxDatePickerProps {
  className?: string;
  disabled?: boolean;
  disableToolbar?: boolean;
  error?: string;
  id?: string;
  label: React.ReactNode;
  name?: string;
  onBlur?: () => void;
  onChange?: (value: string | null) => void;
  positionAbsoluteError?: boolean,
  value?: string | null;
  valueFormat?: string;
  displayFormat?: string;
  required?: boolean;
}

const calculateValue = (value: string | Nil, lastValue: Dayjs | null, valueFormat?: string): dayjs.Dayjs | null => {
  if (isNil(value)) {
    return value === null ? lastValue : null;
  }

  const date = dayjs(value, valueFormat);

  return date.isValid() ? date : null;
}

const NxDatePicker = (
  {
    className,
    disabled = false,
    disableToolbar = false,
    error,
    id,
    label,
    name,
    onBlur,
    onChange: parentOnChange,
    positionAbsoluteError = true,
    value,
    valueFormat = DEFAULT_DATE_PICKER_VALUE_FORMAT,
    displayFormat = DEFAULT_DATE_PICKER_DISPLAY_FORMAT,
    required
  }: NxDatePickerProps
): React.ReactElement => {

  const datePickerClasses = { paper: styles.datePicker };
  const adornmentClasses = { root: styles.iconWrapper };

  const lastValue = useRef<Dayjs | null>(null);
  const finalValue = calculateValue(value, lastValue.current, valueFormat);
  lastValue.current = finalValue;

  const onChange = (value: Dayjs | null): void => {
    lastValue.current = value;

    if (!parentOnChange) {
      logMissingOnChange('DatePicker', name);
      return;
    }

    if (value === null || !value.isValid()) {
      parentOnChange(null);
      return;
    }

    const text = value.format(valueFormat);
    parentOnChange(text);
  };

  const popoverProps: Partial<PopoverProps> = {
    classes: datePickerClasses,
    anchorOrigin: {
      horizontal: 'center',
      vertical: 'top'
    }
  };

  return (
    <div className={fieldWrapperClassName(className)}>
      <MuiPickersUtilsProvider utils={DayJsUtils}>
        <KeyboardDatePicker id={id}
                            disabled={disabled}
                            label={label}
                            disableToolbar={disableToolbar}
                            error={!!error}
                            variant='inline'
                            format={displayFormat}
                            // validation should be handled by client using this component
                            invalidDateMessage={null}
                            maxDateMessage={null}
                            minDateMessage={null}
                            name={name}
                            value={finalValue}
                            onBlur={onBlur}
                            onChange={onChange}
                            className={fieldClassName}
                            required={required}
                            InputAdornmentProps={{classes: adornmentClasses}}
                            InputProps={{classes: inputClasses(disabled, false)}}
                            InputLabelProps={{classes: labelClasses}}
                            PopoverProps={popoverProps}
                            KeyboardButtonProps={{'aria-label': 'change date'}} />
      </MuiPickersUtilsProvider>
      {
        !!error && <div className={errorClassName(positionAbsoluteError)}>{error}</div>
      }
    </div>
  );
}

export default NxDatePicker;
